package top.wcpe.allincore.dsl.compiler.codegen

import top.wcpe.allincore.dsl.compiler.ast.DerivedAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ModelAst
import top.wcpe.allincore.dsl.compiler.ast.NamespacedStateAst
import top.wcpe.allincore.dsl.compiler.ast.ParamsAst
import top.wcpe.allincore.dsl.compiler.ast.StateAst
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.codegen.internal.ExprSerializer
import top.wcpe.allincore.dsl.compiler.codegen.internal.NumberNormalizer
import top.wcpe.allincore.dsl.ir.expr.IrType
import top.wcpe.allincore.dsl.ir.model.UiDerived
import top.wcpe.allincore.dsl.ir.model.UiModel
import top.wcpe.allincore.dsl.ir.model.UiParam
import top.wcpe.allincore.dsl.ir.model.UiParams
import top.wcpe.allincore.dsl.ir.model.UiState

/**
 * 把 [StateAst] 转换为 [UiState]。state 块缺省时返回 [UiState.empty]。
 *
 * entries 值仅允许字面量（语义阶段保证），统一归一化到 String / Long / Double / Boolean。
 *
 * M5-c #I5：若 DSL 作者在 state 字段上显式书写了类型标注，则把 [TypeAst] 映射为 [IrType]
 * 写入 [UiState.getTypes]，供 wire 层持久化、runtime 层写入 guard 使用。
 * 未书写类型的字段不入 types 表，等价于 AnyT，保持向后兼容。
 */
class StateEmitter {

    fun emit(state: StateAst?): UiState {
        if (state == null) return UiState.empty()
        // 使用 LinkedHashMap 保留声明顺序；允许 null value（M6 review must-fix A：null 字面量直接写入）
        val map = LinkedHashMap<String, Any?>()
        val types = LinkedHashMap<String, IrType>()
        for (e in state.entries) {
            map[e.name] = literalToValue(e.literal)
            // 仅对显式书写了类型的字段写入 types 表。
            val irType = e.type?.let(::toIrType)
            if (irType != null) types[e.name] = irType
        }
        // UiState 构造签名为 Map<String, Object>，Kotlin Any? 可平滑传入
        @Suppress("UNCHECKED_CAST")
        return UiState(map as Map<String, Any>, types as Map<String, IrType>)
    }

    /**
     * 把 [DerivedAst] 转换为 [UiDerived]。derived 块缺省时返回 [UiDerived.empty]。
     *
     * 每条 derived 表达式用 [ExprSerializer] 序列化为 ExprBlob 字节，运行时读回包成 Computed。
     * 类型标注规则与 [emit] 一致：仅显式书写类型才写入 types 表。
     */
    fun emitDerived(derived: DerivedAst?): UiDerived {
        if (derived == null) return UiDerived.empty()
        val exprs = LinkedHashMap<String, ByteArray>()
        val types = LinkedHashMap<String, IrType>()
        for (e in derived.entries) {
            exprs[e.name] = ExprSerializer.serializeExpr(e.expr)
            val irType = e.type?.let(::toIrType)
            if (irType != null) types[e.name] = irType
        }
        return UiDerived(exprs, types)
    }

    /** 把 v2 model 地基转换为 IR；legacy 文件没有 model 时返回空模型。 */
    fun emitModel(model: ModelAst?): UiModel {
        if (model == null) return UiModel.empty()
        return UiModel(
            emitParams(model.params),
            emitNamespaceStates(model.sessionNamespaces),
            emitNamespaceStates(model.persistNamespaces),
        )
    }

    private fun emitParams(params: ParamsAst?): UiParams {
        if (params == null) return UiParams.empty()
        val entries = LinkedHashMap<String, UiParam>()
        for (entry in params.entries) {
            val type = toIrType(entry.type)
            val hasDefault = entry.defaultValue != null
            val defaultValue = entry.defaultValue?.let(::literalToValue)
            entries[entry.name] = UiParam(entry.name, type, defaultValue, hasDefault)
        }
        return UiParams(entries)
    }

    private fun emitNamespaceStates(namespaces: List<NamespacedStateAst>): Map<String, UiState> {
        val out = LinkedHashMap<String, UiState>()
        for (namespace in namespaces) {
            out[namespace.namespace] = emit(namespace.state)
        }
        return out
    }

    /**
     * 把 state 默认值字面量节点求值为 wire 层可序列化的 Java 值。
     *
     * StateBlockParser 已经强制语义为递归字面量，所以这里只需机械下钻：
     *  - 基础字面量 → String / Long / Double / Boolean
     *  - NullLit → Java null
     *  - ListLiteralExpr → java.util.ArrayList，元素递归
     *  - MapLiteralExpr → java.util.LinkedHashMap，保留键顺序，值递归
     */
    private fun literalToValue(expr: ExprAst): Any? = when (expr) {
        is ExprAst.Literal.StringLit -> expr.value
        is ExprAst.Literal.NumberLit -> NumberNormalizer.normalize(expr.value)
        is ExprAst.Literal.BoolLit -> expr.value
        is ExprAst.NullLit -> null
        is ExprAst.ListLiteralExpr -> expr.elements.map(::literalToValue)
        is ExprAst.MapLiteralExpr -> {
            val nested = LinkedHashMap<String, Any?>()
            expr.entries.forEach { nested[it.key] = literalToValue(it.value) }
            nested
        }
        else -> error("state 默认值非字面量未被 StateBlockParser 拦截：${expr::class.simpleName}")
    }

    /**
     * 把编译期 [TypeAst] 映射为 wire 侧的 [IrType]。
     *
     * - Nullable 形态：根据 inner 类型映射到对应的 NULLABLE_* 枚举值，
     *   参照 Kotlin 的可空类型语义（如 String? → NULLABLE_STRING）。
     *   无法识别的 inner 类型退化为 [IrType.NULLABLE_ANY]。
     * - 未识别的命名类型（如用户自定义类）映射为 [IrType.ANY]，避免编译期假阳性。
     * - Function 类型当前没有独立 wire 编号，同样映射为 [IrType.ANY]。
     */
    private fun toIrType(t: TypeAst): IrType {
        val nullable = when (t) {
            is TypeAst.Named -> t.nullable
            is TypeAst.Generic -> t.nullable
            is TypeAst.Function -> t.nullable
        }
        if (nullable) {
            // 根据 inner 类型映射到细粒度 NULLABLE_* 枚举值
            return when (t) {
                is TypeAst.Named -> when (t.name) {
                    "Int", "Long", "Double", "Number" -> IrType.NULLABLE_NUMBER
                    "String" -> IrType.NULLABLE_STRING
                    "Bool", "Boolean" -> IrType.NULLABLE_BOOLEAN
                    else -> IrType.NULLABLE_ANY
                }
                is TypeAst.Generic -> when (t.name) {
                    "List" -> IrType.NULLABLE_LIST
                    "Map" -> IrType.NULLABLE_MAP
                    else -> IrType.NULLABLE_ANY
                }
                is TypeAst.Function -> IrType.NULLABLE_ANY
            }
        }
        return when (t) {
            is TypeAst.Named -> when (t.name) {
                "Int", "Long", "Double", "Number" -> IrType.NUMBER
                "String" -> IrType.STRING
                "Bool", "Boolean" -> IrType.BOOLEAN
                "Any" -> IrType.ANY
                // Unit/Color 等在 state 语义下无实际运行时值类型，退化为 Any 避免误判。
                else -> IrType.ANY
            }
            is TypeAst.Generic -> when (t.name) {
                "List" -> IrType.LIST
                "Map" -> IrType.MAP
                else -> IrType.ANY
            }
            is TypeAst.Function -> IrType.ANY
        }
    }
}
